import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Plus, Sparkles, GripVertical, Calendar, Trash2, CheckCircle2, Clock, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { GraftonPlansSection } from "./GraftonPlansSection";

interface Action {
  id: string;
  label: string;
  status: string;
  due_date: string | null;
  created_at: string;
}

const STATUS_COLUMNS = [
  { key: "pending", label: "À faire", icon: Clock, color: "text-amber-500" },
  { key: "in_progress", label: "En cours", icon: AlertCircle, color: "text-blue-500" },
  { key: "done", label: "Terminé", icon: CheckCircle2, color: "text-emerald-500" },
];

export function ActionPlanTab() {
  const { user } = useAuth();
  const [actions, setActions] = useState<Action[]>([]);
  const [loading, setLoading] = useState(true);
  const [newLabel, setNewLabel] = useState("");
  const [newDueDate, setNewDueDate] = useState("");
  const [newStatus, setNewStatus] = useState("pending");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [generating, setGenerating] = useState(false);

  const fetchActions = async () => {
    if (!user) return;
    const { data, error } = await supabase
      .from("actions")
      .select("*")
      .eq("user_id", user.id)
      .order("created_at", { ascending: false });
    if (!error) setActions(data || []);
    setLoading(false);
  };

  useEffect(() => { fetchActions(); }, [user]);

  const addAction = async () => {
    if (!user || !newLabel.trim()) return;
    const { error } = await supabase.from("actions").insert({
      user_id: user.id,
      label: newLabel.trim(),
      status: newStatus,
      due_date: newDueDate || null,
    });
    if (error) { toast.error("Erreur lors de l'ajout"); return; }
    toast.success("Action ajoutée");
    setNewLabel(""); setNewDueDate(""); setNewStatus("pending");
    setDialogOpen(false);
    fetchActions();
  };

  const updateStatus = async (id: string, status: string) => {
    const { error } = await supabase.from("actions").update({ status }).eq("id", id);
    if (!error) {
      setActions(prev => prev.map(a => a.id === id ? { ...a, status } : a));
      toast.success("Statut mis à jour");
    }
  };

  const deleteAction = async (id: string) => {
    const { error } = await supabase.from("actions").delete().eq("id", id);
    if (!error) {
      setActions(prev => prev.filter(a => a.id !== id));
      toast.success("Action supprimée");
    }
  };

  const generateWithAI = async () => {
    if (!user) return;
    setGenerating(true);
    try {
      const { data, error } = await supabase.functions.invoke("grafton-chat", {
        body: {
          messages: [
            { role: "system", content: "Tu es Grafton, un assistant stratégique pour dirigeants. Génère exactement 5 actions commerciales concrètes et prioritaires pour cette semaine. Réponds UNIQUEMENT avec un tableau JSON: [{\"label\": \"...\", \"priority\": \"haute|moyenne|basse\"}]. Pas de texte autour." },
            { role: "user", content: "Génère mon plan d'actions commercial de la semaine." }
          ]
        }
      });
      if (error) throw error;

      const content = data?.choices?.[0]?.message?.content || data?.content || "";
      const jsonMatch = content.match(/\[[\s\S]*?\]/);
      if (!jsonMatch) throw new Error("Format inattendu");

      const suggestions: { label: string }[] = JSON.parse(jsonMatch[0]);
      for (const s of suggestions) {
        await supabase.from("actions").insert({
          user_id: user.id,
          label: s.label,
          status: "pending",
        });
      }
      toast.success(`${suggestions.length} actions générées par Grafton`);
      fetchActions();
    } catch (e) {
      console.error(e);
      toast.error("Erreur lors de la génération IA");
    }
    setGenerating(false);
  };

  const getActionsByStatus = (status: string) => actions.filter(a => a.status === status);

  return (
    <div className="space-y-6">
      {/* Grafton AI plans — validable from this tab */}
      <GraftonPlansSection />

      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div>
          <h2 className="text-xl font-bold text-foreground">Mon plan manuel</h2>
          <p className="text-sm text-muted-foreground">Vos actions personnelles à suivre</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={generateWithAI} disabled={generating} variant="outline" className="gap-2">
            <Sparkles className="h-4 w-4" />
            {generating ? "Génération…" : "Générer avec Grafton"}
          </Button>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="gap-2"><Plus className="h-4 w-4" />Nouvelle action</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Ajouter une action</DialogTitle></DialogHeader>
              <div className="space-y-4 pt-2">
                <Input placeholder="Intitulé de l'action" value={newLabel} onChange={e => setNewLabel(e.target.value)} />
                <Input type="date" value={newDueDate} onChange={e => setNewDueDate(e.target.value)} />
                <Select value={newStatus} onValueChange={setNewStatus}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {STATUS_COLUMNS.map(c => (
                      <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button onClick={addAction} className="w-full" disabled={!newLabel.trim()}>Ajouter</Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* KPI bar */}
      <div className="grid grid-cols-3 gap-3">
        {STATUS_COLUMNS.map(col => {
          const count = getActionsByStatus(col.key).length;
          return (
            <Card key={col.key} className="border-border/50">
              <CardContent className="py-3 px-4 flex items-center gap-3">
                <col.icon className={`h-5 w-5 ${col.color}`} />
                <div>
                  <p className="text-2xl font-bold text-foreground">{count}</p>
                  <p className="text-xs text-muted-foreground">{col.label}</p>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Kanban */}
      {loading ? (
        <div className="text-center py-12 text-muted-foreground">Chargement…</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {STATUS_COLUMNS.map(col => (
            <div key={col.key} className="space-y-2">
              <div className="flex items-center gap-2 mb-3 px-1">
                <col.icon className={`h-4 w-4 ${col.color}`} />
                <h3 className="font-semibold text-sm text-foreground">{col.label}</h3>
                <Badge variant="secondary" className="text-xs ml-auto">{getActionsByStatus(col.key).length}</Badge>
              </div>

              <div className="space-y-2 min-h-[120px] p-2 rounded-lg bg-muted/30 border border-dashed border-border/50">
                {getActionsByStatus(col.key).length === 0 ? (
                  <p className="text-xs text-muted-foreground text-center py-8">Aucune action</p>
                ) : (
                  getActionsByStatus(col.key).map(action => (
                    <Card key={action.id} className="group hover:border-primary/30 transition-colors">
                      <CardContent className="p-3 space-y-2">
                        <div className="flex items-start gap-2">
                          <GripVertical className="h-4 w-4 text-muted-foreground/40 mt-0.5 shrink-0" />
                          <p className="text-sm text-foreground flex-1">{action.label}</p>
                          <Button
                            variant="ghost" size="icon"
                            className="h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity"
                            onClick={() => deleteAction(action.id)}
                          >
                            <Trash2 className="h-3 w-3 text-destructive" />
                          </Button>
                        </div>
                        <div className="flex items-center justify-between">
                          {action.due_date && (
                            <span className="text-xs text-muted-foreground flex items-center gap-1">
                              <Calendar className="h-3 w-3" />
                              {format(new Date(action.due_date), "dd MMM", { locale: fr })}
                            </span>
                          )}
                          <Select value={action.status} onValueChange={v => updateStatus(action.id, v)}>
                            <SelectTrigger className="h-6 text-xs w-auto min-w-[90px] border-0 bg-secondary/50">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {STATUS_COLUMNS.map(s => (
                                <SelectItem key={s.key} value={s.key} className="text-xs">{s.label}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </CardContent>
                    </Card>
                  ))
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
